import React, { useEffect, useState } from 'react';
import { BadgeCheck, ClipboardList, Mail, Save, Shield, User } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../components/AuthProvider';
import { roleDisplayLabel } from '../lib/auth-roles';
import { fetchMember, updateMember } from '../lib/members-api';
import { parseIndianMobile } from '../lib/phone';
import { cn } from '../lib/utils';
import type { Member, MemberRole } from '../types';

function roleBadgeClass(role: MemberRole): string {
  if (role === 'admin') return 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)]';
  if (role === 'organizer') return 'bg-[var(--color-info-subtle)] text-[var(--color-text-secondary)]';
  if (role === 'member') return 'bg-[var(--color-success-subtle)] text-[var(--color-success-text)]';
  return 'bg-[var(--color-surface-2)] text-[var(--color-text-tertiary)]';
}

function RoleIcon({ role }: { role: MemberRole }) {
  if (role === 'admin') return <Shield className="w-3.5 h-3.5" />;
  if (role === 'organizer') return <ClipboardList className="w-3.5 h-3.5" />;
  if (role === 'member') return <BadgeCheck className="w-3.5 h-3.5" />;
  return <User className="w-3.5 h-3.5" />;
}

export function ProfilePage() {
  const { member: authMember, refresh } = useAuth();
  const [profile, setProfile] = useState<Member | null>(authMember);
  const [phone, setPhone] = useState(authMember?.phone ?? '');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!authMember) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    void fetchMember(authMember.uid)
      .then((member) => {
        if (cancelled) return;
        setProfile(member);
        setPhone(member.phone ?? '');
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Could not load profile');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authMember]);

  const handleSave = async () => {
    if (!profile) return;
    const parsedPhone = parseIndianMobile(phone);
    if (!parsedPhone.ok) {
      setError(parsedPhone.error ?? 'Enter a valid mobile number');
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await updateMember(profile.uid, {
        phone: parsedPhone.e164,
      });
      setProfile(updated);
      setPhone(updated.phone ?? '');
      await refresh();
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (!authMember) return null;

  return (
    <Layout>
      <div className="space-y-6 max-w-2xl">
        <header>
          <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">My Profile</h1>
          <p className="text-[var(--color-text-secondary)] text-sm mt-1">
            View your membership details and update your contact information.
          </p>
        </header>

        {loading ? (
          <div className="h-64 bg-[var(--color-surface-2)] rounded-[var(--radius-lg)] animate-pulse" />
        ) : profile ? (
          <div className="bg-[var(--color-surface-1)] rounded-[var(--radius-lg)] border border-[var(--color-border)] p-6 space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-[var(--radius-md)] bg-[var(--color-surface-2)] flex items-center justify-center overflow-hidden shrink-0">
                {profile.photoURL ? (
                  <img
                    src={profile.photoURL}
                    alt={profile.name}
                    referrerPolicy="no-referrer"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-2xl font-semibold text-[var(--color-text-secondary)]">
                    {profile.name[0]}
                  </span>
                )}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">{profile.name}</h2>
                <p className="text-sm text-[var(--color-text-secondary)] mt-1 inline-flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" />
                  {profile.email}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">
                  Name
                </label>
                <p className="py-3 px-4 text-sm text-[var(--color-text-primary)] bg-[var(--color-surface-2)] rounded-[var(--radius-md)] border border-[var(--color-border)]">
                  {profile.name}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">
                  Phone
                </label>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setSaved(false);
                  }}
                  placeholder="10-digit mobile number"
                  required
                  className="w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-3 px-4 text-sm outline-none focus:border-[var(--color-accent)]"
                />
                <p className="text-xs text-[var(--color-text-tertiary)] mt-1.5">
                  Used to contact you about events. Indian mobile number.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">
                  Role
                </label>
                <div
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-2.5 rounded-[var(--radius-md)] text-sm font-medium',
                    roleBadgeClass(profile.role),
                  )}
                >
                  <RoleIcon role={profile.role} />
                  {roleDisplayLabel(profile.role)}
                </div>
                <p className="text-xs text-[var(--color-text-tertiary)] mt-1.5">
                  Role changes are managed by administrators.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">
                  LM Number
                </label>
                <p className="py-3 px-4 text-sm text-[var(--color-text-primary)] bg-[var(--color-surface-2)] rounded-[var(--radius-md)] border border-[var(--color-border)]">
                  {profile.lmNumber ?? (profile.role === 'member' ? '—' : 'Not applicable')}
                </p>
                {profile.role === 'user' && (
                  <p className="text-xs text-[var(--color-text-tertiary)] mt-1.5">
                    Assigned when an admin verifies your membership.
                  </p>
                )}
              </div>
            </div>

            {error && <p className="text-sm text-[var(--color-error)]">{error}</p>}
            {saved && !error && (
              <p className="text-sm text-[var(--color-success-text)]">Profile updated.</p>
            )}

            <div className="pt-2">
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={saving}
                className="inline-flex items-center gap-2 bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white font-medium py-2.5 px-5 rounded-[var(--radius-md)] text-sm disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-[var(--color-error)]">{error ?? 'Could not load profile.'}</p>
        )}
      </div>
    </Layout>
  );
}
